/**
 * Conversion des documents `legacy_measure` (firmware Avibox : avibox, labelbox…) vers le format
 * journalier Serenity (`DailyMeasureEntry`), pour que le widget Régulateur les affiche sans
 * connaître le legacy. Une grandeur legacy de plus = sa lecture ici + sa ligne dans
 * `LEGACY_METRIC_ACCESS_RIGHT` (widgetCatalog).
 *
 * Unités (firmware `avibox/mytuffigorapidex.cpp` et `utilconso.cpp`) : températures en °C,
 * hygrométrie en %, poids moyen et GMQ en **kg** (3 décimales) → convertis en g, eau en L et
 * aliment en kg (compteurs bruts, tracés tels quels par l'écran de la box).
 */
import { DailyMeasureEntry, LegacyMeasureDoc } from "../../../../api/regulator";

type Row = Record<string, unknown>;
type Post = { data?: Row[] };

type DailyData = NonNullable<DailyMeasureEntry["data"]>;
type Animals = NonNullable<DailyData["animals"]>;

const KIND_OF_GENRE: Record<string, string> = {
  MIX: "mixed",
  MAL: "male",
  FEM: "female",
};

const num = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const dayOf = (iso?: string) => iso?.slice(0, 10);

const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);

/**
 * Plage min/max d'un type de sonde sur la journée. Une sonde dont TOUS les relevés valent 0 est
 * considérée absente (le firmware envoie 0 pour une sonde non branchée) ; un 0 isolé reste une
 * vraie valeur.
 */
function probeRange(rows: Row[], type: string) {
  const byProbe = new Map<unknown, number[]>();
  rows.forEach((row) => {
    const value = num(row.mesure);
    if (row.type !== type || value === undefined) return;
    byProbe.set(row.numero, [...(byProbe.get(row.numero) ?? []), value]);
  });
  const values = Array.from(byProbe.values())
    .filter((probe) => probe.some((value) => value !== 0))
    .flat();
  if (values.length === 0) return undefined;
  return { min: Math.min(...values), max: Math.max(...values) };
}

/** Lignes de tous les envois d'un tableau. */
const rowsOf = (posts?: Post[]) =>
  (posts ?? []).flatMap((post) => post.data ?? []);

/** Lignes du DERNIER envoi : mortalités envoyées 1×/jour, un renvoi remplace le précédent. */
const lastRowsOf = (posts?: Post[]) => posts?.[posts.length - 1]?.data ?? [];

export function legacyToDailyMeasures(
  docs: LegacyMeasureDoc[],
  entranceDate?: string
): DailyMeasureEntry[] {
  const days = new Map<string, DailyData>();
  const entry = (date: string) => {
    let data = days.get(date);
    if (!data) {
      data = { date };
      if (entranceDate) data.day = daysBetween(dayOf(entranceDate)!, date);
      days.set(date, data);
    }
    return data;
  };
  const animal = (data: DailyData, genre: unknown) => {
    const kind = KIND_OF_GENRE[String(genre)] ?? "mixed";
    data.animals = data.animals ?? {};
    return (data.animals[kind] = data.animals[kind] ?? {}) as Animals[string];
  };

  // Pesées et consos portent la date de chaque ligne (jour en cours ET veille) et sont renvoyées
  // plusieurs fois par jour : le dernier envoi qui cite une date l'emporte. Le total « veille »
  // envoyé le lendemain remplace ainsi le cumul partiel du jour.
  const weighings = new Map<string, Map<string, Row>>(); // date → genre → ligne
  const counters = new Map<string, Map<string, number>>(); // date → "eau|1" → valeur

  docs.forEach((doc) => {
    const date = dayOf(doc.date);
    if (!date) return;
    const data = entry(date);

    const mesures = rowsOf(doc.mesures);
    const inside = probeRange(mesures, "temp_int");
    const outside = probeRange(mesures, "temp_ext");
    const humidity = probeRange(mesures, "hygro_int");
    if (inside || outside || humidity) {
      data.ambience = {
        temperature: { inside, outside },
        humidity: { inside: humidity },
      };
    }

    lastRowsOf(doc.mortalites).forEach((row) => {
      animal(data, row.genre).dead = {
        natural: num(row.naturelle),
        eliminated: num(row.elimination),
        cardiac: num(row.cardiaque),
        takenout: num(row.sortie),
      };
    });

    rowsOf(doc.pesees).forEach((row) => {
      const rowDate = String(row.date ?? date);
      const byGenre = weighings.get(rowDate) ?? new Map<string, Row>();
      byGenre.set(String(row.genre), row);
      weighings.set(rowDate, byGenre);
    });

    rowsOf(doc.conso_general).forEach((row) => {
      const value = num(row.valeur);
      if ((row.type !== "eau" && row.type !== "aliment") || value === undefined)
        return;
      const rowDate = String(row.date ?? date);
      const byCounter = counters.get(rowDate) ?? new Map<string, number>();
      byCounter.set(`${row.type}|${row.numero}`, value);
      counters.set(rowDate, byCounter);
    });
  });

  // Une ligne « veille » du premier jour reçu tombe hors fenêtre : on ne crée pas de jour pour elle.
  weighings.forEach((byGenre, date) => {
    const data = days.get(date);
    if (!data) return;
    byGenre.forEach((row) => {
      const average = num(row.poids_moyen);
      // Poids moyen à 0 = pas de pesée ce jour-là (un animal ne pèse jamais 0).
      if (!average) return;
      const gmq = num(row.gmq);
      animal(data, row.genre).weight = {
        average: average * 1000,
        gmq: gmq === undefined ? undefined : gmq * 1000,
        homogeneity: num(row.homogeneite),
      };
    });
  });

  counters.forEach((byCounter, date) => {
    const data = days.get(date);
    if (!data) return;
    const read = (type: string) =>
      Array.from(byCounter.entries())
        .filter(([key]) => key.startsWith(`${type}|`))
        .map(([, value]) => value);
    const water = read("eau");
    const food = read("aliment");
    data.consumption = {
      water: water.length > 0 ? water : null,
      food: food.length > 0 ? food : null,
    };
  });

  return Array.from(days.values())
    .sort((a, b) => (a.date! < b.date! ? -1 : 1))
    .map((data) => ({ data }));
}
